import type { ReactNode } from "react";
import { SelectionOverlay } from "./SelectionOverlay";
import { UploadProgressOverlay } from "./UploadProgressOverlay";

type ImageGridItemProps = {
  imageUrl: string;
  imageName: string;
  isSelected: boolean;
  isUploading?: boolean;
  startTime?: number | null | undefined;
  borderColor?: "gray" | "gradient";
  onSelect: () => void;
  actionButton?: ReactNode;
  metadata?: ReactNode;
  disabled?: boolean;
};

export function ImageGridItem({
  imageUrl,
  imageName,
  isSelected,
  isUploading = false,
  startTime,
  borderColor = "gray",
  onSelect,
  actionButton,
  metadata,
  disabled = false
}: ImageGridItemProps) {
  const borderClass = borderColor === "gradient" ? "bg-cta-gradient" : "bg-gray-200";

  const getImageOpacity = () => {
    if (isUploading) return "opacity-60";
    if (isSelected) return "opacity-80";
    return "opacity-100 group-hover:opacity-80";
  };

  const isClickable = !isUploading && !disabled;

  return (
    <div className="relative w-full text-sm">
      {actionButton && !isUploading && (
        <div className="absolute right-2 top-2 z-20">{actionButton}</div>
      )}
      <div
        className={`${borderClass} ${isSelected || isUploading || disabled ? "" : "group"} relative aspect-square ${isClickable ? "cursor-pointer" : ""} rounded-lg`}
        onClick={isClickable ? onSelect : undefined}
      >
        <div className="absolute inset-px flex items-center justify-center rounded-lg bg-white">
          <img
            src={imageUrl}
            alt={imageName}
            className={`size-full rounded-lg object-contain transition-opacity duration-200 ${getImageOpacity()}`}
          />

          {!isUploading && !disabled && (
            <SelectionOverlay isSelected={isSelected} onSelect={onSelect} hoverAlignment="center" />
          )}

          {isUploading && <UploadProgressOverlay startTime={startTime} />}
        </div>
      </div>
      {metadata && <div className="mt-2 space-y-1">{metadata}</div>}
    </div>
  );
}
